import React, { useEffect } from 'react';
import { fetchEditableContact } from 'flow/actions/contact-actions';
import GenericSmallIconButton from 'flow/components/buttons/GenericSmallIconButton';
import ContactInfoFields from 'flow/components/contact-card/ContactInfoFields';
import ContactInfoFieldsFallback from 'flow/components/contact-card/ContactInfoFieldsFallback';
import ContactSettingsItems from 'flow/components/contact-card/ContactSettingsItems';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectUserCountryCode } from 'flow/selectors/ExtensionSelectors';
import { ContactFull } from 'flow/types/Contact';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { isThirdPartyContactKey } from 'flow/utils';
import { selectEditableContactDTO } from 'flow/selectors/contact-selectors';

interface Props {
    contact: TypedMap<ContactFull>;
    extensionKey?: ExtensionKey;
}

const ContactInfoCard = ({ contact, extensionKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();
    const ownCountryCode = useAppSelector(selectUserCountryCode);
    const contactKey = contact?.get('key');
    const contactEditable = useAppSelector((state) =>
        selectEditableContactDTO(state, contactKey),
    );
    const contactEditableExists = !!contactEditable;

    useEffect(() => {
        if (contactEditableExists) return;
        void dispatch(fetchEditableContact(contactKey));
    }, [dispatch, contactKey, contactEditableExists]);

    const handleEditContact = () => {
        openModal('editContact', { contact });
    };

    const handleCreateContact = () => {
        openModal('createContact', { contact, countryCode: ownCountryCode });
    };

    if (!contact) return null;

    const contactIsSaved =
        contact?.has('key') && !isThirdPartyContactKey(contact.get('key'));

    const contactInfoFieldsProps = {
        contact,
        extensionKey,
    };

    return (
        <div className="bg-gray-150 w-full flex-1 rounded-sm p-1">
            <header className="flex items-center justify-between py-5 ps-6 pe-4">
                <div className="text-2xl/5 font-bold text-gray-800">
                    {i18next.t('Settings.Account.Contact')}
                </div>
                {contactIsSaved && contact.get('editable') && (
                    <GenericSmallIconButton
                        onClick={handleEditContact}
                        iconKey="edit"
                        label={i18next.t('Tooltip.Nav.Title.Edit')}
                        className="bg-transparent text-gray-500"
                        size="small"
                    />
                )}
                {!contactIsSaved && (
                    <GenericSmallIconButton
                        onClick={handleCreateContact}
                        iconKey="add"
                        label={i18next.t('Colleagues.Create.External.Title')}
                        className="bg-white text-gray-400"
                        size="small"
                    />
                )}
            </header>
            {contactIsSaved && (
                <ContactSettingsItems
                    contact={contact}
                    extensionKey={extensionKey}
                />
            )}
            {contact.has('editableContactDTO') ? (
                <ContactInfoFields {...contactInfoFieldsProps} />
            ) : (
                <ContactInfoFieldsFallback {...contactInfoFieldsProps} />
            )}
        </div>
    );
};

export default React.memo(ContactInfoCard);
